import { Dialog, DialogPanel } from "@headlessui/react";
import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useCookies from "react-cookie/cjs/useCookies";

import {
  ModalBackdrop,
  ModalContainer,
  CrossBtn,
  LoginOptionsPanel,
  LoginOptionsHeader,
  LoginOptionsBody,
  LoginOptionsSectionTitle,
  LoginOptionsSectionDescription,
  LoginOptionsDivider,
  LoginOptionsButtonsArea,
  LoginOptionsPrimaryButton,
  LoginOptionsSecondaryButton,
} from "./styles";

import Cross from "components/IconImage/cross";

import { configState, sendULFEvent, setModal } from "state/config-slice";
import { ULFEvents } from "types/ULF";

const LoginOptionsModal: FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentConfig = useSelector(configState);
  const modal = currentConfig && currentConfig.modal;
  const [cookies] = useCookies();

  const { showModal } = modal;

  const close = () => {
    dispatch(setModal({ showModal: false }));
  };

  const silverUrl = currentConfig.openidUrls?.find((el) => el.type === "silver")?.url;
  const bronzeUrl = currentConfig.openidUrls?.find((el) => el.type === "bronze")?.url;

  const dispatchBronzeLogin = useCallback(() => {
    dispatch(
      sendULFEvent({
        page: window.location.pathname,
        "event-name": ULFEvents.BRONZE_LOGIN,
        source: cookies.SOURCE,
        isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
      }),
    );
  }, [dispatch, cookies.SOURCE, cookies.SUBSCRIPTIONPLUS_TRACKING]);

  const dispatchSilverLogin = useCallback(() => {
    dispatch(
      sendULFEvent({
        page: window.location.pathname,
        "event-name": ULFEvents.SILVER_LOGIN,
        source: cookies.SOURCE,
        isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
      }),
    );
  }, [dispatch, cookies.SOURCE, cookies.SUBSCRIPTIONPLUS_TRACKING]);

  const handleSilverLogin = useCallback(() => {
    if (silverUrl) {
      dispatchSilverLogin();
      window.location.href = silverUrl;
    }
  }, [dispatchSilverLogin, silverUrl]);

  const handleBronzeLogin = useCallback(() => {
    if (bronzeUrl) {
      dispatchBronzeLogin();
      window.location.href = bronzeUrl;
    }
  }, [dispatchBronzeLogin, bronzeUrl]);

  return (
    <Dialog as={ModalContainer} open={showModal} onClose={close} data-testid="login-options-modal">
      <ModalBackdrop />

      <DialogPanel as={LoginOptionsPanel}>
        <CrossBtn onClick={close} aria-label={t("Close")} title={t("Close")} data-testid="login-options-close">
          <Cross />
        </CrossBtn>

        <LoginOptionsHeader data-testid="login-options-header">{t("Log in options")}</LoginOptionsHeader>

        <LoginOptionsBody>
          <LoginOptionsSectionTitle data-testid="login-options-email-title">
            {t("Vodafone registered Email & Password")}
          </LoginOptionsSectionTitle>
          <LoginOptionsSectionDescription data-testid="login-options-email-description">
            {t("Access offers for eligible products on your account")}
          </LoginOptionsSectionDescription>

          <LoginOptionsDivider />

          <LoginOptionsSectionTitle data-testid="login-options-mobile-title">
            {t("Vodafone Mobile number")}
          </LoginOptionsSectionTitle>
          <LoginOptionsSectionDescription data-testid="login-options-mobile-description">
            {t("View offers specific to that number")}
          </LoginOptionsSectionDescription>
        </LoginOptionsBody>

        <LoginOptionsButtonsArea>
          <LoginOptionsPrimaryButton
            title={t("Email and password")}
            data-testid="login-options-email-btn"
            onClick={handleSilverLogin}
          />
          <LoginOptionsSecondaryButton
            title={t("Mobile number")}
            data-testid="login-options-msisdn-btn"
            onClick={handleBronzeLogin}
          />
        </LoginOptionsButtonsArea>
      </DialogPanel>
    </Dialog>
  );
};

export default LoginOptionsModal;
